/* eslint-disable prefer-destructuring */
import LocationActions, { LATLNG_PROVIDERS } from '../../actions/location';
import ValidationRunner from './runner';
import { getRulesByCountry } from './locationRules';
import { genericFireAction, translateValidationResult } from './util';
import ExperimentUtil from '../../utils/ExperimentUtil';
import {
    generateDefaultServerValidation,
    generateDefaultValidation,
    validateFieldChangeServerAddress,
    validateServerAddress
} from '../../utils/AddressUtil';
import { isValidLatin } from './rules';
import TranslationUtil from '../../utils/TranslationUtil';
import { isAddressValid } from '../../components/PageFlow/Predicates';
import { isConfirmAddressFeatureEnabled } from '../../utils/ExperimentsUtil';
import log from '../../utils/ClientLogger';
import { isCountryInGoogleMapsExclusionList } from '../../utils/UserLocationUtil';

const getAddressToValidate = (address, addressValidation, field, value) => {
    const result = {};
    Object.keys(addressValidation).forEach((x) => {
        result[x] = address[x];
    });
    if (field) result[field] = value;
    return result;
};

const validateAddress = (store, address, isLatin) => {
    const {
        app
    } = store.getState();
    const vrboRules = app?.features?.isFrboFlow;
    const validation = generateDefaultValidation();
    const rules = getRulesByCountry(address.country || 'US', isLatin, vrboRules);
    Object.keys(address).forEach((x) => {
        const result = ValidationRunner(address[x], rules[x]);
        validation[x] = translateValidationResult(result, store, { isSet: !!address[x] });
        validation[x].isLatin = !address[x] || isValidLatin.validate(address[x]);
    });
    return validation;
};

const timers = {};

const logValidationErrors = (result, address) => {
    if (timers.validationErrors) {
        clearTimeout(timers.validationErrors);
    }
    timers.validationErrors = setTimeout(() => {
        Object.keys(result).forEach((item) => {
            if (!result[item].isValid) {
                log('info', 'client side validation', { ...result[item], ...{ ...address, [item]: address[item] || '' } });
            }
        });
    }, 500);
};

export default (store) => (next) => async (action) => {
    // Guard against null/undefined actions or non-action objects (like Promises)
    if (!action || typeof action !== 'object' || !action.type) {
        return next(action);
    }

    let result;
    let addressToValidate;
    let actionGenerator;
    let doNext = true;

    const {
        location, app, joinDetails, router
    } = store.getState();
    const { pinLocation } = location;
    const experimentUtil = new ExperimentUtil(app.experiments);
    const isAddressV2 = app?.features?.isConventionalLodgingFlow || isConfirmAddressFeatureEnabled(experimentUtil);
    const { translations, fallbackTranslations } = app;
    const translationUtil = new TranslationUtil(translations, fallbackTranslations);
    const { propertyNameValidation, latinPropertyNameValidation } = joinDetails;

    const isPropertyNameValid = () => propertyNameValidation.isValid || latinPropertyNameValidation.isValid;

    switch (action.type) {
    case LocationActions.SET_LAT_LNG:
        if (action.latLng.lat) {
            next(action);
            doNext = false;
            actionGenerator = LocationActions.triggerGeoCoderValidation;
            genericFireAction(store, actionGenerator);
        }
        break;
    case LocationActions.VALIDATE_GEOCODER:
        genericFireAction(store, LocationActions.clearServerAddressValidation);
        actionGenerator = LocationActions.geocoderValidate;
        genericFireAction(store, actionGenerator, {
            address: location.address,
            latLng: location.latLng,
            latLngFallback: isAddressV2,
            propertyName: ((isAddressV2 || app?.features?.isConventionalLodgingFlow) && isPropertyNameValid()) ? location.propertyName : '',
            pathSource: app.pathSource
        });
        break;
    case LocationActions.VALIDATE_GEOCODER_SUCCESS:
    case LocationActions.VALIDATE_GEOCODER_FAIL:
        result = validateServerAddress(
            translationUtil,
            location.address,
            location.serverAddressValidation,
            {
                unitNumberValidation: location.unitNumberValidation.value,
                validationErrorList: action.errorList,
                latLngError: location.geocoderLatLng.error
            }
        );
        actionGenerator = LocationActions.setServerAddresValidation;
        genericFireAction(store, actionGenerator, result);
        break;
    case LocationActions.TRIGGER_MIN_COORDINATES:
    case LocationActions.GEOCODER_LAT_LNG:
        if (router?.location?.pathname.includes('location')) {
            actionGenerator = LocationActions.geocoderLatLng;
            genericFireAction(store, actionGenerator, {
                address: location.address,
                candidateLatLng: action.candidateLatLng
                    || (pinLocation && pinLocation.lat),
                propertyName: ((isAddressV2 || app?.features?.isConventionalLodgingFlow) && isPropertyNameValid()) ? location.propertyName : '',
                pathSource: app.pathSource,
                provider: (isAddressV2)
                    ? LATLNG_PROVIDERS.GOOGLE : LATLNG_PROVIDERS.GEOCODER,
                minCoordinates: action.country
            });
        }
        break;
    case LocationActions.GEOCODER_LAT_LNG_SUCCESS:
    case LocationActions.GEOCODER_LAT_LNG_FAIL:
        result = validateServerAddress(
            translationUtil,
            location.address,
            generateDefaultServerValidation(),
            {
                unitNumberValidation: location.unitNumberValidation.value,
                validationErrorList: [],
                latLngError: action.error
            }
        );
        actionGenerator = LocationActions.setServerAddresValidation;
        genericFireAction(store, actionGenerator, result);
        // if the map page is not shown, fire the geocoder validation manually
        // since it won't be triggered by the map
        if (isAddressV2 || isCountryInGoogleMapsExclusionList(app.userCountryCode)) {
            if (!pinLocation || !pinLocation.lat) {
                genericFireAction(store, LocationActions.setPinLocation, action.value);
            }
            next(action);
            doNext = false;
            actionGenerator = LocationActions.triggerGeoCoderValidation;
            genericFireAction(store, actionGenerator);
        }
        break;
    case LocationActions.VALIDATE_UNIT_NUMBER:
        genericFireAction(store, LocationActions.clearServerAddressValidation);
        actionGenerator = LocationActions.validateUnitNumber;
        genericFireAction(store, actionGenerator, location.address);
        break;
    case LocationActions.VALIDATE_UNIT_NUMBER_SUCCESS:
    case LocationActions.VALIDATE_UNIT_NUMBER_FAIL:
        result = validateServerAddress(
            translationUtil,
            location.address,
            location.serverAddressValidation,
            {
                unitNumberValidation: action.value,
                validationErrorList: location.geocoderValidation.errorList,
                latLngError: location.geocoderLatLng.error
            }
        );
        actionGenerator = LocationActions.setServerAddresValidation;
        genericFireAction(store, actionGenerator, result);
        break;
    case LocationActions.SET_ADDRESS_FIELD:
        addressToValidate = getAddressToValidate(
            location.address,
            location.addressValidation,
            action.field,
            action.value
        );

        validateFieldChangeServerAddress(
            location.address,
            action,
            location.unitNumberValidation.value,
            translationUtil,
            store
        );

        result = validateAddress(store, addressToValidate, action.useLatin);

        if (isAddressValid({ addressValidation: result })
            && action.field !== 'address2'
        ) {
            actionGenerator = LocationActions.triggerGeocoderLatLng;
            genericFireAction(store, actionGenerator, { candidateLatLng: action.candidateLatLng });
        }
        actionGenerator = LocationActions.setAddressValidation;
        genericFireAction(store, actionGenerator, result);

        logValidationErrors(result, addressToValidate);
        break;
    default:
        break;
    }

    // Avoid dispatching a null action downstream. If this middleware decides
    // not to forward, simply stop propagation instead of calling next(null).
    if (!doNext) {
        return undefined; // swallow action intentionally
    }
    return next(action);
};
